/**
 * Fetch wrapper for the SSS API (docs/api.md). Adds the bearer token,
 * X-Device-Id and X-App-Version, normalises errors to ApiError with the
 * server's stable `code`, and records the last successful server contact
 * (used for the offline session limit, ADR-0021).
 */
import { APP_VERSION, getDeviceId } from '../device';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** No response at all (offline, DNS, CORS, timeout). */
  get isNetwork(): boolean {
    return this.status === 0;
  }
  /** Worth retrying later: network problems and server-side failures. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

export interface TokenSource {
  /** Current ID token, refreshed when `force` is set. Null when signed out. */
  getToken(force?: boolean): Promise<string | null>;
}

export interface ApiClientOptions {
  baseUrl: string;
  tokens: TokenSource;
  fetchImpl?: typeof fetch;
  onServerContact?: () => void;
  onAuthFailure?: (e: ApiError) => void;
  deviceId?: string;
  appVersion?: string;
}

export interface RequestOptions {
  body?: unknown;
  form?: FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Return the raw Response (binary downloads). */
  raw?: boolean;
  /** Skip the Authorization header (public endpoints). */
  anonymous?: boolean;
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  get baseUrl(): string {
    return this.opts.baseUrl;
  }

  private async send(method: string, path: string, o: RequestOptions, token: string | null): Promise<Response> {
    const headers: Record<string, string> = {
      'X-Device-Id': this.opts.deviceId ?? getDeviceId(),
      'X-App-Version': this.opts.appVersion ?? APP_VERSION,
      ...o.headers,
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (o.form) body = o.form;
    else if (o.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(o.body);
    }
    const f = this.opts.fetchImpl ?? fetch;
    try {
      return await f(this.opts.baseUrl + path, { method, headers, body, signal: o.signal });
    } catch (e) {
      throw new ApiError(0, 'NETWORK', e instanceof Error ? e.message : 'Network error');
    }
  }

  async request<T>(method: string, path: string, o: RequestOptions = {}): Promise<T> {
    const token = o.anonymous ? null : await this.opts.tokens.getToken();
    let res = await this.send(method, path, o, token);
    if (res.status === 401 && !o.anonymous && token) {
      // Token may have expired between refreshes — retry once with a fresh one.
      const fresh = await this.opts.tokens.getToken(true);
      if (fresh && fresh !== token) res = await this.send(method, path, o, fresh);
    }
    if (res.status < 500) this.opts.onServerContact?.();

    if (!res.ok) {
      let payload: { code?: string; message?: string; details?: unknown } = {};
      try {
        payload = await res.json();
      } catch {
        /* not JSON */
      }
      const err = new ApiError(res.status, payload.code ?? `HTTP_${res.status}`, payload.message ?? res.statusText, payload.details);
      if (res.status === 401 || (res.status === 403 && isAccountError(err.code))) this.opts.onAuthFailure?.(err);
      throw err;
    }
    if (o.raw) return res as unknown as T;
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  get<T>(path: string, o?: RequestOptions) {
    return this.request<T>('GET', path, o);
  }
  post<T>(path: string, body?: unknown, o?: RequestOptions) {
    return this.request<T>('POST', path, { ...o, body: body ?? {} });
  }
  put<T>(path: string, body?: unknown, o?: RequestOptions) {
    return this.request<T>('PUT', path, { ...o, body: body ?? {} });
  }
  patch<T>(path: string, body?: unknown, o?: RequestOptions) {
    return this.request<T>('PATCH', path, { ...o, body: body ?? {} });
  }
  delete<T>(path: string, o?: RequestOptions) {
    return this.request<T>('DELETE', path, o);
  }
  upload<T>(path: string, fields: Record<string, string | undefined>, file: Blob, fileName: string) {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v !== undefined) form.append(k, v);
    form.append('file', file, fileName);
    return this.request<T>('POST', path, { form });
  }
  async blob(path: string): Promise<Blob> {
    const res = await this.request<Response>('GET', path, { raw: true });
    return res.blob();
  }
}

export function isAccountError(code: string): boolean {
  return ['NOT_PROVISIONED', 'DISABLED', 'DOMAIN_NOT_ALLOWED', 'WRONG_METHOD', 'MFA_REQUIRED', 'EMAIL_NOT_VERIFIED'].includes(code);
}

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
}
